import { panelNavHtml } from '../panel-nav-html.js';

export type OfferId = 'foundry' | 'ai_forge' | 'team-training';

export interface OfferData {
  id: OfferId;
  name: string;
  summary: string;
  format: string;
  who: string;
  deliverables: string[];
}

export interface ShowOffersPayload {
  highlight: OfferId | null;
  offers: OfferData[];
}

/**
 * Offer comparison — three glass cards slide up over the orb when
 * Merlin calls show_offers. Optional highlight tier gets a "Recommended"
 * ribbon and a brighter border. Cards are dismissed with the close
 * button or automatically when the call ends.
 */
export class OfferCards {
  private readonly root: HTMLElement;

  constructor(root: HTMLElement) {
    this.root = root;
    this.root.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target === this.root || target.closest('[data-action="close"]')) {
        this.hide();
        document.dispatchEvent(new CustomEvent('merlin:nav-close', { bubbles: true }));
        return;
      }
      if (target.closest('[data-action="back"]')) {
        document.dispatchEvent(new CustomEvent('merlin:nav-back', { bubbles: true }));
      }
    });
    this.render([], null);
  }

  show(payload: ShowOffersPayload) {
    this.render(payload.offers, payload.highlight);
    this.root.classList.add('visible');
  }

  hide() {
    this.root.classList.remove('visible');
  }

  private render(offers: OfferData[], highlight: OfferId | null) {
    this.root.classList.add('package-overlay');
    if (offers.length === 0) {
      this.root.innerHTML = '';
      return;
    }
    const cards = offers
      .map((o) => this.cardHtml(o, highlight === o.id))
      .join('');
    this.root.innerHTML = `
      <div class="package-sheet">
        ${panelNavHtml()}
        <div class="package-header">
          <div>
            <div class="package-title">How we engage</div>
            <div class="package-sub">Scope-based, fixed-quote proposals · 48-hour turnaround</div>
          </div>
        </div>
        <div class="package-grid">${cards}</div>
      </div>
    `;
  }

  private cardHtml(offer: OfferData, recommended: boolean): string {
    const deliverables = offer.deliverables
      .map((d) => `<li>${escapeHtml(d)}</li>`)
      .join('');
    return `
      <div class="package-card ${recommended ? 'recommended' : ''}">
        ${recommended ? '<div class="package-ribbon">Recommended for you</div>' : ''}
        <div class="package-name">${escapeHtml(offer.name)}</div>
        <div class="package-timeline">${escapeHtml(offer.format)}</div>
        <ul class="package-highlights">${deliverables}</ul>
        <div class="package-pricing">${escapeHtml(offer.who)}</div>
      </div>
    `;
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
